import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Briefcase, Building2, User, Calendar, DollarSign, Target,
  AlertTriangle, CheckCircle, XCircle, ArrowRight, Plus,
  Clock, Wrench,
} from 'lucide-react';
import api from '../api';
import { Deal, Activity, Note, PipelineStage } from '../types';
import Modal from '../components/Modal';

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);

const formatDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString('fr-FR');

const isOverdue = (dateStr: string): boolean => {
  if (!dateStr) return false;
  const due = new Date(dateStr);
  due.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today;
};

const DealDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [deal, setDeal] = useState<Deal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [activities, setActivities] = useState<Activity[]>([]);
  const [notes, setNotes] = useState<Note[]>([]);
  const [stages, setStages] = useState<PipelineStage[]>([]);

  // Modals
  const [showStageModal, setShowStageModal] = useState(false);
  const [selectedStageId, setSelectedStageId] = useState('');
  const [stageChangeLoading, setStageChangeLoading] = useState(false);

  const [showWonModal, setShowWonModal] = useState(false);
  const [showLostModal, setShowLostModal] = useState(false);
  const [lostReason, setLostReason] = useState('');
  const [outcomeLoading, setOutcomeLoading] = useState(false);

  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activityForm, setActivityForm] = useState({
    type: 'call' as string, subject: '', description: '', activityDate: new Date().toISOString().split('T')[0],
  });
  const [activityFormError, setActivityFormError] = useState('');
  const [activityFormLoading, setActivityFormLoading] = useState(false);

  const [newNote, setNewNote] = useState('');
  const [noteLoading, setNoteLoading] = useState(false);

  const fetchDeal = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await api.get(`/deals/${id}`);
      setDeal(res.data);
    } catch (err) {
      setError('Affaire introuvable.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchSubData = useCallback(async () => {
    if (!id) return;
    try {
      const [activitiesRes, notesRes] = await Promise.all([
        api.get('/activities', { params: { dealId: id, limit: 100 } }).catch(() => ({ data: { data: [] } })),
        api.get(`/deals/${id}/notes`).catch(() => ({ data: [] })),
      ]);
      setActivities(activitiesRes.data.data || activitiesRes.data || []);
      setNotes(notesRes.data.data || notesRes.data || []);
    } catch (err) {
      // ignore
    }
  }, [id]);

  const fetchStages = useCallback(async () => {
    try {
      const res = await api.get('/pipeline-stages');
      const data = res.data.data || res.data || [];
      setStages(data);
    } catch (err) {
      // try alternative
      try {
        const res = await api.get('/deals/stages');
        setStages(res.data.data || res.data || []);
      } catch (err2) {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    fetchDeal();
    fetchSubData();
    fetchStages();
  }, [fetchDeal, fetchSubData, fetchStages]);

  const handleStageChange = async () => {
    if (!deal || !selectedStageId) return;
    setStageChangeLoading(true);
    try {
      await api.patch(`/deals/${deal.id}/stage`, { stageId: selectedStageId });
      setShowStageModal(false);
      fetchDeal();
    } catch (err) {
      try {
        await api.patch(`/deals/${deal.id}`, { stageId: selectedStageId });
        setShowStageModal(false);
        fetchDeal();
      } catch (err2) {
        // ignore
      }
    } finally {
      setStageChangeLoading(false);
    }
  };

  const handleMarkWon = async () => {
    if (!deal) return;
    setOutcomeLoading(true);
    try {
      await api.patch(`/deals/${deal.id}/outcome`, { outcome: 'won' });
      setShowWonModal(false);
      fetchDeal();
    } catch (err) {
      try {
        await api.patch(`/deals/${deal.id}`, { outcome: 'won' });
        setShowWonModal(false);
        fetchDeal();
      } catch (err2) {
        // ignore
      }
    } finally {
      setOutcomeLoading(false);
    }
  };

  const handleMarkLost = async () => {
    if (!deal) return;
    setOutcomeLoading(true);
    try {
      await api.patch(`/deals/${deal.id}/outcome`, { outcome: 'lost', lostReason });
      setShowLostModal(false);
      setLostReason('');
      fetchDeal();
    } catch (err) {
      try {
        await api.patch(`/deals/${deal.id}`, { outcome: 'lost', lostReason });
        setShowLostModal(false);
        setLostReason('');
        fetchDeal();
      } catch (err2) {
        // ignore
      }
    } finally {
      setOutcomeLoading(false);
    }
  };

  const handleAddActivity = async () => {
    setActivityFormError('');
    if (!activityForm.subject.trim()) {
      setActivityFormError('Le sujet est obligatoire.');
      return;
    }
    setActivityFormLoading(true);
    try {
      await api.post('/activities', {
        ...activityForm,
        dealId: id,
        companyId: deal?.company?.id,
      });
      setShowActivityModal(false);
      setActivityForm({ type: 'call', subject: '', description: '', activityDate: new Date().toISOString().split('T')[0] });
      fetchSubData();
    } catch (err: any) {
      setActivityFormError(err.response?.data?.message || 'Erreur.');
    } finally {
      setActivityFormLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setNoteLoading(true);
    try {
      await api.post(`/deals/${id}/notes`, { content: newNote });
      setNewNote('');
      fetchSubData();
    } catch (err) {
      // ignore
    } finally {
      setNoteLoading(false);
    }
  };

  if (loading) {
    return <div className="spinner-container"><div className="spinner" /></div>;
  }

  if (error || !deal) {
    return (
      <div className="empty-state">
        <AlertTriangle size={48} />
        <p className="empty-state-title">{error || 'Affaire introuvable'}</p>
        <button className="btn" onClick={() => navigate('/pipeline')}>Retour au pipeline</button>
      </div>
    );
  }

  const nextStepOverdue = deal.nextStepDueDate && isOverdue(deal.nextStepDueDate);

  return (
    <div>
      <div className="detail-header">
        <div>
          <h1>
            <Briefcase size={28} />
            {deal.title}
            {deal.stage && (
              <span className="badge" style={{ background: deal.stage.color + '20', color: deal.stage.color }}>
                {deal.stage.name}
              </span>
            )}
            {deal.outcome === 'won' && <span className="badge badge-success"><CheckCircle size={12} /> Gagnée</span>}
            {deal.outcome === 'lost' && <span className="badge badge-danger"><XCircle size={12} /> Perdue</span>}
          </h1>
        </div>
        <div className="btn-group">
          {!deal.outcome && (
            <>
              <button className="btn" onClick={() => { setSelectedStageId(deal.stage?.id || ''); setShowStageModal(true); }}>
                <ArrowRight size={16} /> Changer étape
              </button>
              <button className="btn btn-success" onClick={() => setShowWonModal(true)}>
                <CheckCircle size={16} /> Gagnée
              </button>
              <button className="btn btn-danger" onClick={() => setShowLostModal(true)}>
                <XCircle size={16} /> Perdue
              </button>
            </>
          )}
        </div>
      </div>

      {/* Next Step */}
      {deal.nextStepDescription && !deal.outcome && (
        <div className={`next-step-card ${nextStepOverdue ? 'overdue' : ''}`}>
          <div className="next-step-label">
            <Clock size={14} style={{ display: 'inline', marginRight: '4px' }} />
            Prochaine étape
          </div>
          <div className="next-step-description">{deal.nextStepDescription}</div>
          {deal.nextStepDueDate && (
            <div className="next-step-date">
              Échéance : {formatDate(deal.nextStepDueDate)}
              {nextStepOverdue && ' (en retard)'}
            </div>
          )}
        </div>
      )}

      {/* Deal lost reason */}
      {deal.outcome === 'lost' && deal.lostReason && (
        <div className="next-step-card overdue mb-3">
          <div className="next-step-label">Raison de la perte</div>
          <div className="next-step-description">{deal.lostReason}</div>
        </div>
      )}

      {/* Info grid */}
      <div className="card mb-3">
        <div className="card-header"><h3>Informations</h3></div>
        <div className="card-body">
          <div className="detail-grid">
            <div className="detail-item">
              <div className="detail-item-label">Entreprise</div>
              <div className="detail-item-value">
                {deal.company ? (
                  <span className="inline-flex cursor-pointer text-primary" onClick={() => navigate(`/companies/${deal.company!.id}`)}>
                    <Building2 size={14} /> {deal.company.name}
                  </span>
                ) : '-'}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">Contact</div>
              <div className="detail-item-value">
                {deal.contact ? (
                  <span className="inline-flex cursor-pointer text-primary" onClick={() => navigate(`/contacts/${deal.contact!.id}`)}>
                    <User size={14} /> {deal.contact.firstName} {deal.contact.lastName}
                  </span>
                ) : '-'}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">Assigné à</div>
              <div className="detail-item-value">
                {deal.assignedTo ? `${deal.assignedTo.firstName} ${deal.assignedTo.lastName}` : '-'}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">Montant estimé</div>
              <div className="detail-item-value">
                {deal.estimatedAmount !== undefined && deal.estimatedAmount !== null ? (
                  <span className="inline-flex font-bold"><DollarSign size={14} /> {formatCurrency(deal.estimatedAmount)}</span>
                ) : '-'}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">Marge estimée</div>
              <div className="detail-item-value">
                {deal.estimatedMargin !== undefined && deal.estimatedMargin !== null ? formatCurrency(deal.estimatedMargin) : '-'}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">Probabilité</div>
              <div className="detail-item-value">
                <span className="inline-flex"><Target size={14} /> {deal.probability}%</span>
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">Type de machine</div>
              <div className="detail-item-value">
                {deal.machineType ? <span className="inline-flex"><Wrench size={14} /> {deal.machineType}</span> : '-'}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">Catégorie machine</div>
              <div className="detail-item-value">{deal.machineCategory || '-'}</div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">Date de clôture prévue</div>
              <div className="detail-item-value">
                {deal.estimatedCloseDate ? (
                  <span className="inline-flex"><Calendar size={14} /> {formatDate(deal.estimatedCloseDate)}</span>
                ) : '-'}
              </div>
            </div>
            <div className="detail-item">
              <div className="detail-item-label">Créée le</div>
              <div className="detail-item-value">{formatDate(deal.createdAt)}</div>
            </div>
          </div>
          {deal.description && (
            <div style={{ marginTop: '16px', padding: '12px', background: '#F9FAFB', borderRadius: '8px' }}>
              <div className="detail-item-label" style={{ marginBottom: '4px' }}>Description</div>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>{deal.description}</div>
            </div>
          )}
        </div>
      </div>

      <div className="two-col">
        {/* Activities */}
        <div className="card">
          <div className="card-header">
            <h3>Activités</h3>
            <button className="btn btn-primary btn-sm" onClick={() => setShowActivityModal(true)}>
              <Plus size={14} /> Ajouter
            </button>
          </div>
          <div className="card-body">
            {activities.length === 0 ? (
              <div className="empty-state" style={{ padding: '20px' }}>
                <p className="text-secondary">Aucune activité</p>
              </div>
            ) : (
              <div className="timeline">
                {activities.map((a) => (
                  <div key={a.id} className="timeline-item">
                    <div className="timeline-item-header">
                      <span className="timeline-item-title">{a.subject}</span>
                      <span className="badge badge-info">{a.type}</span>
                    </div>
                    <div className="timeline-item-date">
                      {formatDate(a.activityDate)}
                      {a.user && ` - ${a.user.firstName} ${a.user.lastName}`}
                    </div>
                    {a.description && <div className="timeline-item-body">{a.description}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="card">
          <div className="card-header"><h3>Notes</h3></div>
          <div className="card-body">
            <div className="form-group">
              <textarea
                className="form-textarea"
                placeholder="Ajouter une note..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                rows={3}
              />
              <button
                className="btn btn-primary btn-sm mt-2"
                onClick={handleAddNote}
                disabled={noteLoading || !newNote.trim()}
              >
                {noteLoading ? <span className="spinner spinner-sm" /> : 'Ajouter'}
              </button>
            </div>

            {notes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '12px', color: '#9CA3AF' }}>Aucune note</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
                {notes.map((n) => (
                  <div key={n.id} className="card" style={{ padding: '10px 14px' }}>
                    <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>{n.content}</div>
                    <div className="text-secondary mt-1" style={{ fontSize: '0.8rem' }}>
                      {n.user && `${n.user.firstName} ${n.user.lastName} - `}
                      {formatDate(n.createdAt)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stage Change Modal */}
      <Modal
        isOpen={showStageModal}
        onClose={() => setShowStageModal(false)}
        title="Changer l'étape"
        size="sm"
        footer={
          <>
            <button className="btn" onClick={() => setShowStageModal(false)}>Annuler</button>
            <button className="btn btn-primary" onClick={handleStageChange} disabled={stageChangeLoading}>
              {stageChangeLoading ? <span className="spinner spinner-sm" /> : 'Confirmer'}
            </button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">Nouvelle étape</label>
          <select className="form-select" value={selectedStageId} onChange={(e) => setSelectedStageId(e.target.value)}>
            <option value="">Sélectionner...</option>
            {stages.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      </Modal>

      {/* Won Modal */}
      <Modal
        isOpen={showWonModal}
        onClose={() => setShowWonModal(false)}
        title="Marquer comme gagnée"
        size="sm"
        footer={
          <>
            <button className="btn" onClick={() => setShowWonModal(false)}>Annuler</button>
            <button className="btn btn-success" onClick={handleMarkWon} disabled={outcomeLoading}>
              {outcomeLoading ? <span className="spinner spinner-sm" /> : 'Confirmer'}
            </button>
          </>
        }
      >
        <p>Confirmez-vous que cette affaire est gagnée ?</p>
        <p className="text-secondary mt-2">
          <strong>{deal.title}</strong>
          {deal.estimatedAmount !== undefined && deal.estimatedAmount !== null && ` - ${formatCurrency(deal.estimatedAmount)}`}
        </p>
      </Modal>

      {/* Lost Modal */}
      <Modal
        isOpen={showLostModal}
        onClose={() => setShowLostModal(false)}
        title="Marquer comme perdue"
        size="sm"
        footer={
          <>
            <button className="btn" onClick={() => setShowLostModal(false)}>Annuler</button>
            <button className="btn btn-danger" onClick={handleMarkLost} disabled={outcomeLoading}>
              {outcomeLoading ? <span className="spinner spinner-sm" /> : 'Confirmer'}
            </button>
          </>
        }
      >
        <p style={{ marginBottom: '12px' }}>Pourquoi cette affaire est-elle perdue ?</p>
        <div className="form-group">
          <label className="form-label">Raison</label>
          <textarea
            className="form-textarea"
            value={lostReason}
            onChange={(e) => setLostReason(e.target.value)}
            placeholder="Ex: Prix trop élevé, concurrent retenu..."
            rows={3}
          />
        </div>
      </Modal>

      {/* Activity Modal */}
      <Modal
        isOpen={showActivityModal}
        onClose={() => setShowActivityModal(false)}
        title="Ajouter une activité"
        footer={
          <>
            <button className="btn" onClick={() => setShowActivityModal(false)}>Annuler</button>
            <button className="btn btn-primary" onClick={handleAddActivity} disabled={activityFormLoading}>
              {activityFormLoading ? <span className="spinner spinner-sm" /> : 'Créer'}
            </button>
          </>
        }
      >
        {activityFormError && <div className="login-error">{activityFormError}</div>}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Type</label>
            <select className="form-select" value={activityForm.type} onChange={(e) => setActivityForm({ ...activityForm, type: e.target.value })}>
              <option value="call">Appel</option>
              <option value="email">Email</option>
              <option value="meeting">Réunion</option>
              <option value="letter">Courrier</option>
              <option value="note">Note</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Date</label>
            <input type="date" className="form-input" value={activityForm.activityDate} onChange={(e) => setActivityForm({ ...activityForm, activityDate: e.target.value })} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Sujet *</label>
          <input className="form-input" value={activityForm.subject} onChange={(e) => setActivityForm({ ...activityForm, subject: e.target.value })} />
        </div>
        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea className="form-textarea" value={activityForm.description} onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })} rows={4} />
        </div>
      </Modal>
    </div>
  );
};

export default DealDetailPage;
